import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { translateEnum } from '../translate-enum.js';

/**
 * Renders an enum field as a dropdown using the project's existing translation
 * keys (`<translationPrefix>_<VALUE>` — e.g. `cv_extract_form_enum_ICSRM_FRA`).
 *
 * Why this exists: `@adornis/generated-form`'s default enum renderer looks up
 * `${formTranslationPrefix}_enum_${fieldName}_${value}` (with a double
 * underscore when the form prefix already ends with `_`, plus an extra field
 * name segment). Our translation messages don't follow that shape, so the
 * default renderer prints raw keys like `cv_extract_form__enum_university_ICSRM_FRA`.
 *
 * Pass this renderer explicitly for every enum field on a generated form:
 *
 *     renderField(['employmentType'], enumRenderer(EmploymentType, 'job_form_enum'))
 *     renderField(['university'], enumRenderer(IcsrmUniEnum, 'cv_extract_form_enum'))
 */
export const enumRenderer =
  <E extends Record<string, string>>(enumType: E, translationPrefix: string, multi = false, maxItems?: number) =>
  (props: any) => {
    // baseql's enum registration mutates string enums to add `_isBaseQLEnum: true`
    // and `_class`; filter those meta keys out before listing options.
    const allSelectables = Object.entries(enumType)
      .filter(([key]) => !['_isBaseQLEnum', '_class'].includes(key))
      .map(([, value]) => value);

    // Cap a multi-select at `maxItems`: once the limit is reached, offer only the
    // already-selected options so no further value can be picked. Selected entries
    // stay removable via their tags, after which the full list returns. This works
    // with DropdownSelectionInput's data flow (it has no native max-selection option).
    const value = props.value;
    const atCap = multi && maxItems != null && Array.isArray(value) && value.length >= maxItems;
    const selectables = atCap ? allSelectables.filter(v => (value as string[]).includes(v)) : allSelectables;

    return DropdownSelectionInput({
      ...props,
      multi,
      selectables,
      renderString: (v: string) => translateEnum(translationPrefix, v),
    });
  };
